import { ChangeDetectorRef, Component, ElementRef, inject, OnInit, viewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';

import { OcJsonSchemaValidatorAjv } from '../../../../core/oc-validator/oc-jsonSchemaValidatorAjv.service';
import { UntilDestroy } from '@ngneat/until-destroy';

import { IError } from '@orgacard/login';

import { AppService } from '../../../../app.service';
import { CoreService } from '../../../../core/core.service';
import { EnvironmentService } from '../../../../core/environment.service';
import { ocValidator } from '../../../../core/oc-validator/oc.validator';
import { ImageService } from '../../../../core/services/image.service';
import { UtilityHelperService } from '../../../../core/utility-helper.service';
import { UtilityHelper } from '../../../../core/utility.helper';
import { LoadingOverlayService } from '../../../../loading-overlay/loading-overlay.service';
import { AddressComponent } from '../../../shared/address/address.component';
import { IAddressDisplay, SystemMethod } from '../../../shared/models/basic.model';
import { EdiService } from '../../../shared/services/edi.service';
import { ISupplier, ISupplierBank, ISupplierContact, ISupplierContactDetail } from '../../shared/models/supplier.model';
import { SupplierService } from '../../shared/services/supplier.service';
import { ContactsComponent } from './contacts/contacts.component';

@UntilDestroy()
@Component({
    selector: 'app-basedata',
    templateUrl: './basedata.component.html',
    styleUrls: ['./basedata.component.scss'],
    standalone: false,
})
export class BasedataComponent implements OnInit {
    private readonly utilityHelperService = inject(UtilityHelperService);
    private appService = inject(AppService);
    private formBuilder = inject(UntypedFormBuilder);
    coreService = inject(CoreService);
    supplierService = inject(SupplierService);
    environmentService = inject(EnvironmentService);
    imageService = inject(ImageService);
    ediService = inject(EdiService);
    ocJsonSchemaValidatorAjv = inject(OcJsonSchemaValidatorAjv);
    loadingOverlayService = inject(LoadingOverlayService);
    cdr = inject(ChangeDetectorRef);

    public readonly suppliername = viewChild<ElementRef>('suppliername');
    public readonly addressComponent = viewChild(AddressComponent);
    public readonly contactComponent = viewChild(ContactsComponent);

    public utilityHelper = new UtilityHelper(this.appService, this.environmentService, this.coreService, this.loadingOverlayService);

    public basedataForm: UntypedFormGroup;

    // convenience getter for easy access to form fields
    public get basedataf() {
        return this.basedataForm.controls;
    }

    public addressData: IAddressDisplay;
    public imagePath: string = '/mws/rest/supplierIcon?language=' + this.environmentService.appSettings.locale + '&supplierIconId=';
    public uploadUrl: string = '';
    public formData: any = {};
    protected isEdiPossible: boolean = false;

    public ngOnInit() {
        this.initAddressData();
        this.initFormValues();
        this.checkEdiPossible();
        //
        this.uploadUrl = this.environmentService.getURL(this.environmentService.appSettings.fakeServer) + '/supplier/icon/create';
        this.imagePath = this.utilityHelper.getImagePath(this.imagePath);
        this.formData = {
            supplierId: '' + this.supplierService.supplierId,
            currentUserCostcenterId: this.environmentService.currentUserCostcenterId,
            iconId: '' + this.supplierService.supplierBasedata.supplierIconId,
            description: '',
            sortnumber: 1,
            imageDomain: 'SUPPLIER',
        };
    }

    public initAddressData() {
        if (
            this.supplierService.supplierBasedata &&
            this.supplierService.supplierBasedata &&
            this.supplierService.supplierBasedata.mainAddress &&
            this.supplierService.supplierBasedata.mainAddress.address
        ) {
            this.addressData = this.supplierService.supplierBasedata.mainAddress.address;
        } else {
            this.addressData = {} as IAddressDisplay;
        }
    }

    public initFormValues() {
        // if (this.supplierService.method === 'NEW') {
        //     this.initEmptyBasedataForm();
        // } else if (this.supplierService.method === 'EDIT' && this.supplierService.supplierBasedata) {
        this.initBasedataForm();
        // } else {
        //     // TODO: Fehlermeldung
        // }
        this.basedataForm.valueChanges.subscribe((change) => {
            // PRÜFEN OB FORM VALID IST !!! NICHT VIA this.xxxxxxForm.valid
            // PRÜFEN OB FORM VALID IST !!! NICHT VIA this.xxxxxxForm.valid
            const result = this.ocJsonSchemaValidatorAjv.validateData('supplier', this.basedataForm.value);
            if (!this.basedataForm.get('changed').touched && result.isValid) {
                this.basedataForm.get('changed').markAsTouched();
            } else if (!result.isValid) {
                this.basedataForm.get('changed').markAsUntouched();
            }
        });
    }

    public dataChangeSupplierContact(contact: { data: ISupplierContact; method: SystemMethod }) {
        this.loadingOverlayService.startLoading();
        switch (contact.method) {
            case SystemMethod.NEW:
                this.supplierService.saveSupplierContact(contact.data).subscribe(
                    (contactResponse) => {
                        this.supplierService.supplierBasedata.supplierContacts.push(contactResponse);
                        this.supplierService.supplierBasedata.supplierContacts = this.supplierService.supplierBasedata.supplierContacts.concat();
                        this.contactComponent().completeContactData();
                        this.loadingOverlayService.stopLoading();
                    },
                    (err: IError) => {
                        this.utilityHelperService.handleComponentError(err);
                    },
                );
                break;
            case SystemMethod.EDIT:
                this.supplierService.saveSupplierContact(contact.data).subscribe(
                    (contactResponse) => {
                        const indexElement = this.supplierService.supplierBasedata.supplierContacts.findIndex((element) => element.id === contact.data.id);
                        this.supplierService.supplierBasedata.supplierContacts[indexElement] = contactResponse;
                        this.supplierService.supplierBasedata.supplierContacts = this.supplierService.supplierBasedata.supplierContacts.concat();
                        this.contactComponent().completeContactData();
                        this.loadingOverlayService.stopLoading();
                    },
                    (err: IError) => {
                        this.utilityHelperService.handleComponentError(err);
                    },
                );
                break;
            case SystemMethod.DELETE:
                this.supplierService.deleteSupplierContact(contact.data.id).subscribe(
                    (response) => {
                        const indexElement = this.supplierService.supplierBasedata.supplierContacts.findIndex((element) => element.id === contact.data.id);
                        this.supplierService.supplierBasedata.supplierContacts.splice(indexElement, 1);
                        this.supplierService.supplierBasedata.supplierContacts = this.supplierService.supplierBasedata.supplierContacts.concat();
                        this.contactComponent().completeContactData();
                        this.loadingOverlayService.stopLoading();
                    },
                    (err: IError) => {
                        this.utilityHelperService.handleComponentError(err);
                    },
                );
                break;
        }
    }

    public dataChangeSupplierContactDetail(contactDetail: { data: ISupplierContactDetail; method: SystemMethod }) {
        this.loadingOverlayService.startLoading();
        const indexContact = this.supplierService.supplierBasedata.supplierContacts.findIndex((element) => element.id === contactDetail.data.supplierContactId);
        switch (contactDetail.method) {
            case SystemMethod.NEW:
                this.supplierService.saveSupplierContactDetail(contactDetail.data).subscribe(
                    (contactDetailResponse) => {
                        this.supplierService.supplierBasedata.supplierContacts[indexContact].contactDetails.push(contactDetailResponse);
                        this.contactComponent().contacts = this.supplierService.supplierBasedata.supplierContacts.concat();
                        this.contactComponent().completeContactData();
                        this.loadingOverlayService.stopLoading();
                    },
                    (err: IError) => {
                        this.utilityHelperService.handleComponentError(err);
                    },
                );
                break;
            case SystemMethod.EDIT:
                this.supplierService.saveSupplierContactDetail(contactDetail.data).subscribe(
                    (contactDetailResponse) => {
                        const indexElement = this.supplierService.supplierBasedata.supplierContacts[indexContact].contactDetails.findIndex(
                            (element) => element.id === contactDetail.data.id,
                        );
                        this.supplierService.supplierBasedata.supplierContacts[indexContact].contactDetails[indexElement] = contactDetailResponse;
                        this.contactComponent().contacts = this.supplierService.supplierBasedata.supplierContacts.concat();
                        this.contactComponent().completeContactData();
                        this.loadingOverlayService.stopLoading();
                    },
                    (err: IError) => {
                        this.utilityHelperService.handleComponentError(err);
                    },
                );
                break;
            case SystemMethod.DELETE:
                this.supplierService.deleteSupplierContactDetail(contactDetail.data.id).subscribe(
                    (response) => {
                        const indexElement = this.supplierService.supplierBasedata.supplierContacts[indexContact].contactDetails.findIndex(
                            (element) => element.id === contactDetail.data.id,
                        );
                        this.supplierService.supplierBasedata.supplierContacts[indexContact].contactDetails.splice(indexElement, 1);
                        this.contactComponent().contacts = this.supplierService.supplierBasedata.supplierContacts.concat();
                        this.loadingOverlayService.stopLoading();
                    },
                    (err: IError) => {
                        this.utilityHelperService.handleComponentError(err);
                    },
                );
                break;
        }
    }

    public dataChangeBank(supplierBank: { data: ISupplierBank; method: SystemMethod }) {
        this.loadingOverlayService.startLoading();
        switch (supplierBank.method) {
            case SystemMethod.NEW:
                this.supplierService.saveSupplierBankdata(supplierBank.data).subscribe(
                    (supplierBankResponse) => {
                        this.supplierService.supplierBasedata.supplierBanks.push(supplierBankResponse);
                        this.supplierService.supplierBasedata.supplierBanks = this.supplierService.supplierBasedata.supplierBanks.concat();
                        this.loadingOverlayService.stopLoading();
                    },
                    (err: IError) => {
                        this.utilityHelperService.handleComponentError(err);
                    },
                );
                break;
            case SystemMethod.EDIT:
                this.supplierService.saveSupplierBankdata(supplierBank.data).subscribe(
                    (supplierBankResponse) => {
                        const indexElement = this.supplierService.supplierBasedata.supplierBanks.findIndex((element) => element.id === supplierBank.data.id);
                        this.supplierService.supplierBasedata.supplierBanks[indexElement] = supplierBankResponse;
                        this.supplierService.supplierBasedata.supplierBanks = this.supplierService.supplierBasedata.supplierBanks.concat();
                        this.loadingOverlayService.stopLoading();
                    },
                    (err: IError) => {
                        this.utilityHelperService.handleComponentError(err);
                    },
                );
                break;
            case SystemMethod.DELETE:
                this.supplierService.deleteSupplierBank(supplierBank.data.id).subscribe(
                    (response) => {
                        const indexElement = this.supplierService.supplierBasedata.supplierBanks.findIndex((element) => element.id === supplierBank.data.id);
                        this.supplierService.supplierBasedata.supplierBanks.splice(indexElement, 1);
                        this.supplierService.supplierBasedata.supplierBanks = this.supplierService.supplierBasedata.supplierBanks.concat();
                        this.loadingOverlayService.stopLoading();
                    },
                    (err: IError) => {
                        this.utilityHelperService.handleComponentError(err);
                    },
                );
                break;
        }
    }

    public saveSupplierBaseData() {
        // PRÜFEN OB FORM VALID IST !!! NICHT VIA this.xxxxxxForm.valid
        // PRÜFEN OB FORM VALID IST !!! NICHT VIA this.xxxxxxForm.valid
        const result = this.ocJsonSchemaValidatorAjv.validateData('supplier', this.basedataForm.value);
        if (result.isValid) {
            this.supplierService.supplierBasedata = this.getSupplierBasedataFromForm();
            this.loadingOverlayService.startLoading();
            this.supplierService.saveSupplierBasedata(this.supplierService.supplierBasedata).subscribe(
                (data) => {
                    // console.log(this.supplierService.supplierBasedata);
                    this.initFormValues();
                    this.loadingOverlayService.stopLoading();
                },
                (err: IError) => {
                    this.utilityHelperService.handleComponentError(err);
                },
            );
        }
    }

    public getSupplierBasedataFromForm(): ISupplier {
        return {
            id: this.basedataForm.get('id').value,
            name: this.basedataForm.get('name').value,
            number: this.basedataForm.get('number').value,
            currencyId: this.basedataForm.get('currencyId').value,
            supplierIconId: this.supplierService.supplierBasedata.supplierIconId,
            imageId: this.supplierService.supplierBasedata.imageId,
            supplierContacts: this.supplierService.supplierBasedata.supplierContacts,
            mainAddress: this.supplierService.supplierBasedata.mainAddress,
            description: this.basedataForm.get('description').value,
            externalSupplierId: this.basedataForm.get('externalSupplierId').value,
            glnCode: this.basedataForm.get('glnCode').value,
            facebookUrl: this.basedataForm.get('facebookUrl').value,
            // financialCompanyCode: this.basedataForm.get('financialCompanyCode').value,
            isRegional: this.basedataForm.get('isRegional').value,
            mainMandatorId: this.supplierService.supplierBasedata.mainMandatorId,
            orderEmail: this.basedataForm.get('orderEmail').value,
            orderFax: this.basedataForm.get('orderFax').value,
            orderPhone: this.basedataForm.get('orderPhone').value,
            orderUrl: this.basedataForm.get('orderUrl').value,
            isEdi: this.basedataForm.get('isEdi').value,
            regionalDescription: this.basedataForm.get('regionalDescription').value,
            supplierBanks: this.supplierService.supplierBasedata.supplierBanks,
            supplierStatus: this.supplierService.supplierBasedata.supplierStatus,
            taxNumber: this.basedataForm.get('taxNumber').value,
            validFrom: this.supplierService.supplierBasedata.validFrom,
            validTill: this.supplierService.supplierBasedata.validTill,
            webUrl: this.basedataForm.get('webUrl').value,
            attributeLocale: this.supplierService.supplierBasedata.attributeLocale,
            updatable: this.supplierService.supplierBasedata.updatable,
        };
    }

    public saveSupplierMainAddress(address: IAddressDisplay) {
        this.loadingOverlayService.startLoading();
        this.supplierService.saveSupplierAddress(address).subscribe(
            (element) => {
                this.cdr.detectChanges();
                // initAddressData funktioniert nicht, Input Emitter addressData zieht nicht !?
                // this.initAddressData();
                // addressData direkt in Komponente aktualisieren!
                this.addressComponent().addressData = this.supplierService.supplierBasedata.mainAddress.address;
                this.addressComponent().fillAddressFormValues();
                this.loadingOverlayService.stopLoading();
            },
            (err: IError) => {
                this.utilityHelperService.handleComponentError(err);
            },
        );
    }

    public deleteSupplierIcon() {
        this.loadingOverlayService.startLoading();
        this.supplierService.deleteSupplierIcon(this.supplierService.supplierBasedata.supplierIconId).subscribe(
            (response) => {
                this.loadingOverlayService.stopLoading();
                this.supplierService.supplierBasedata.supplierIconId = undefined;
                this.supplierService.supplierBasedata.imageId = undefined;
            },
            (err: IError) => {
                this.utilityHelperService.handleComponentError(err);
            },
        );
    }

    public setUploaderReturnValue(value: any) {
        // console.log('setUploaderReturnValue: ' + JSON.stringify(value));
        this.supplierService.supplierBasedata.supplierIconId = value['iconId'];
        this.supplierService.supplierBasedata.imageId = value['imageId'];
    }

    public checkRegional(event: any) {
        if (this.basedataf.isRegional.value) {
            console.log('isRegional: TRUE');
            // this.basedataf.regionalDescription.enable();
        } else {
            console.log('isRegional: FALSE');
            // this.basedataf.regionalDescription.disable();
        }
    }

    checkEdiPossible() {
        this.ediService.checkEdiProvider().subscribe({
            next: (result) => {
                this.isEdiPossible = result;
            },
            error: (err) => {
                this.utilityHelperService.handleComponentError(err);
            },
        });
    }
    private initBasedataForm() {
        // !this.ability.can('update', <any> ('supplier.name')) }),
        this.basedataForm = this.formBuilder.group({
            id: [
                {
                    value: this.supplierService.supplierBasedata.id,
                    disabled: true,
                },
            ],
            name: [
                {
                    value: this.supplierService.supplierBasedata.name,
                    disabled: !this.supplierService.supplierBasedata.updatable || !this.coreService.checkAbility('update', 'supplier.name'),
                },
                [
                    ocValidator(
                        this.ocJsonSchemaValidatorAjv,
                        this.environmentService,
                        'supplier',
                        'name',
                        this.coreService.getMsgkey('mws.suppliername', undefined),
                    ),
                ],
            ],
            number: [
                {
                    value: this.supplierService.supplierBasedata.number,
                    disabled: !this.supplierService.supplierBasedata.updatable || !this.coreService.checkAbility('update', 'supplier.number'),
                },
                [
                    ocValidator(
                        this.ocJsonSchemaValidatorAjv,
                        this.environmentService,
                        'supplier',
                        'name',
                        this.coreService.getMsgkey('mws.suppliernumber', undefined),
                    ),
                ],
            ],
            description: [
                {
                    value: this.supplierService.supplierBasedata.description,
                    disabled: !this.supplierService.supplierBasedata.updatable || !this.coreService.checkAbility('update', 'supplier.description'),
                },
                [
                    ocValidator(
                        this.ocJsonSchemaValidatorAjv,
                        this.environmentService,
                        'supplier',
                        'description',
                        this.coreService.getMsgkey('mws.supplierdescription', undefined),
                    ),
                ],
            ],
            currencyId: [
                {
                    // value: this.supplierService.supplierBasedata.currencyId ? this.supplierService.supplierBasedata.currencyId : this.environmentService.currencyList.find(cur => cur.iso4217Code === 'EUR').id,
                    value: this.supplierService.supplierBasedata.currencyId,
                    disabled: !this.supplierService.supplierBasedata.updatable || !this.coreService.checkAbility('update', 'supplier.currency'),
                },
            ],
            externalSupplierId: [
                {
                    value: this.supplierService.supplierBasedata.externalSupplierId,
                    disabled: !this.supplierService.supplierBasedata.updatable || !this.coreService.checkAbility('update', 'supplier.externalSupplierId'),
                },
            ],
            glnCode: [
                {
                    value: this.supplierService.supplierBasedata.glnCode,
                    disabled: !this.supplierService.supplierBasedata.updatable || !this.coreService.checkAbility('update', 'supplier.glnCode'),
                },
            ],
            taxNumber: [
                {
                    value: this.supplierService.supplierBasedata.taxNumber,
                    disabled: !this.supplierService.supplierBasedata.updatable || !this.coreService.checkAbility('update', 'supplier.taxnumber'),
                },
                [ocValidator(this.ocJsonSchemaValidatorAjv, this.environmentService, 'supplier', 'taxNumber')],
            ],
            // financialCompanyCode: [this.supplierService.supplierBasedata.financialCompanyCode, ocValidator(this.ocJsonSchemaValidatorAjv, this.environmentService, 'supplier', 'financialCompanyCode')],
            isRegional: [
                {
                    value: this.supplierService.supplierBasedata.isRegional,
                    disabled: !this.supplierService.supplierBasedata.updatable || !this.coreService.checkAbility('update', 'supplier.isregional'),
                },
            ],
            regionalDescription: [
                {
                    value: this.supplierService.supplierBasedata.regionalDescription,
                    disabled: !this.supplierService.supplierBasedata.updatable || !this.coreService.checkAbility('update', 'supplier.regionaldescription'),
                },
                [
                    ocValidator(
                        this.ocJsonSchemaValidatorAjv,
                        this.environmentService,
                        'supplier',
                        'regionalDescription',
                        this.coreService.getMsgkey('mws.regionalsupplier', undefined),
                    ),
                ],
            ],
            orderPhone: [
                {
                    value: this.supplierService.supplierBasedata.orderPhone,
                    disabled: !this.supplierService.supplierBasedata.updatable || !this.coreService.checkAbility('update', 'supplier.order.phone'),
                },
                [ocValidator(this.ocJsonSchemaValidatorAjv, this.environmentService, 'supplier', 'orderPhone', this.coreService.getMsgkey('mws.telephone'))],
            ],
            orderFax: [
                {
                    value: this.supplierService.supplierBasedata.orderFax,
                    disabled: !this.supplierService.supplierBasedata.updatable || !this.coreService.checkAbility('update', 'supplier.order.fax'),
                },
                [ocValidator(this.ocJsonSchemaValidatorAjv, this.environmentService, 'supplier', 'orderFax', this.coreService.getMsgkey('mws.fax', undefined))],
            ],
            orderEmail: [
                {
                    value: this.supplierService.supplierBasedata.orderEmail,
                    disabled: !this.supplierService.supplierBasedata.updatable || !this.coreService.checkAbility('update', 'supplier.order.email'),
                },
                [
                    ocValidator(
                        this.ocJsonSchemaValidatorAjv,
                        this.environmentService,
                        'supplier',
                        'orderEmail',
                        this.coreService.getMsgkey('mws.email', undefined),
                    ),
                ],
            ],
            orderUrl: [
                {
                    value: this.supplierService.supplierBasedata.orderUrl,
                    disabled: !this.supplierService.supplierBasedata.updatable || !this.coreService.checkAbility('update', 'supplier.order.orderurl'),
                },
                [
                    ocValidator(
                        this.ocJsonSchemaValidatorAjv,
                        this.environmentService,
                        'supplier',
                        'orderUrl',
                        this.coreService.getMsgkey('mws.onlineportal', undefined),
                    ),
                ],
            ],
            isEdi: [
                {
                    value: this.supplierService.supplierBasedata.isEdi,
                    disabled: !this.supplierService.supplierBasedata.updatable || !this.coreService.checkAbility('update', 'supplier.order.isedi'),
                },
            ],
            webUrl: [
                {
                    value: this.supplierService.supplierBasedata.webUrl,
                    disabled: !this.supplierService.supplierBasedata.updatable || !this.coreService.checkAbility('update', 'supplier.order.weburl'),
                },
                [
                    ocValidator(
                        this.ocJsonSchemaValidatorAjv,
                        this.environmentService,
                        'supplier',
                        'webUrl',
                        this.coreService.getMsgkey('mws.homepage', undefined),
                    ),
                ],
            ],
            // [
            //     Validators.pattern('(https?://)?([\\da-z.-]+)\\.([a-z.]{2,6})[/\\w .-]*/?')
            // ]],
            facebookUrl: [
                {
                    value: this.supplierService.supplierBasedata.facebookUrl,
                    disabled: !this.supplierService.supplierBasedata.updatable || !this.coreService.checkAbility('update', 'supplier.facebookurl'),
                },
                [
                    ocValidator(
                        this.ocJsonSchemaValidatorAjv,
                        this.environmentService,
                        'supplier',
                        'facebookUrl',
                        this.coreService.getMsgkey('mws.facebook', undefined),
                    ),
                ],
            ],
            // [
            //     Validators.pattern('(https?://)?([\\da-z.-]+)\\.([a-z.]{2,6})[/\\w .-]*/?')
            // ]],
            changed: false,
        });
        //
        // VORSICHT: this.basedataf.regionalDescription.enable()
        //           überschreibt den Validator ??? Dann immer minlength=1 obwohl nicht definiert ???
        if (this.supplierService.supplierBasedata.isRegional) {
            // this.basedataf.regionalDescription: [({
            //     value: '',
            //     disabled: !this.coreService.checkAbility('update', 'supplier.regionaldescription ') }),
            //     [ocValidator(this.ocJsonSchemaValidatorAjv, this.environmentService, 'supplier', 'regionalDescription')]];
            // this.basedataf.regionalDescription.enable();
        } else {
            // this.basedataf.regionalDescription: [({
            //     value: '',
            //     disabled: !this.coreService.checkAbility('update', 'supplier.regionaldescription ') }),
            //     [ocValidator(this.ocJsonSchemaValidatorAjv, this.environmentService, 'supplier', 'regionalDescription')]];
            // this.basedataf.regionalDescription.disable();
        }
    }
}
